import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface RegionCardProps {
  region: {
    id: string;
    name: string;
    countries: string[];
    image: string;
    alt: string;
    coverage: string;
    activeRoutes: number;
    borderCrossings: number;
    certifications: string[];
  };
  onSelect: (regionId: string) => void;
  isSelected: boolean;
}

const RegionCard = ({ region, onSelect, isSelected }: RegionCardProps) => {
  return (
    <div
      onClick={() => onSelect(region.id)}
      className={`relative overflow-hidden rounded-lg cursor-pointer transition-all duration-300 ${
        isSelected
          ? 'ring-2 ring-golden-mid shadow-golden-lg'
          : 'hover:shadow-elevation-2'
      }`}
    >
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={region.image}
          alt={region.alt}
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="font-headline font-bold text-xl text-white mb-1">
            {region.name}
          </h3>
          <p className="font-body text-sm text-white/90">{region.coverage}</p>
        </div>
      </div>

      <div className="bg-card p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {region.countries.map((country) => (
            <span
              key={country}
              className="px-2 py-1 bg-muted rounded text-xs font-body text-muted-foreground"
            >
              {country}
            </span>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center space-x-2">
            <Icon name="TruckIcon" size={16} className="text-golden-mid" />
            <div>
              <p className="text-xs font-body text-muted-foreground">
                Active Routes
              </p>
              <p className="text-sm font-headline font-semibold text-foreground">
                {region.activeRoutes}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Icon name="MapPinIcon" size={16} className="text-golden-mid" />
            <div>
              <p className="text-xs font-body text-muted-foreground">
                Border Points
              </p>
              <p className="text-sm font-headline font-semibold text-foreground">
                {region.borderCrossings}
              </p>
            </div>
          </div>
        </div>

        <div className="pt-3 border-t border-border">
          <div className="flex items-center justify-between">
            <span className="text-xs font-body text-muted-foreground">
              Certifications
            </span>
            <div className="flex items-center space-x-1">
              {region.certifications.slice(0, 3).map((cert, idx) => (
                <Icon
                  key={idx}
                  name="ShieldCheckIcon"
                  size={16}
                  className="text-success"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegionCard;